import {
  isPublicTransferMode,
  slugify,
  textToHtml,
  type CreateTransferRequest,
  type TransferDetail,
  type TransferMode,
  type TransferRateInput,
  type TransferServiceType,
  type Weekday,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Plus, Sparkles, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useApproveTransfer,
  useCreateTransfer,
  useTransferDetail,
  useUpdateTransfer,
} from '../use-transfers';

import { type GalleryPhoto } from '@/components/photo-gallery';
import {
  CityPicker,
  CountryPicker,
  CurrencyPicker,
  LanguagePicker,
  SupplierPicker,
  VehicleTypePicker,
} from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { StarRatingInput } from '@/components/star-rating';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { relativeTime } from '@/features/dashboard/dashboard-utils';
import { FixedMediaSlotsField } from '@/features/website-cms/components/FixedMediaSlotsField';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Full-page Transfer edit, in the shared catalog-editor mold (matches Hotel /
 * Activity): slim top bar + read-only legacy metadata strip + two tabs
 * (Details / Content & media), each a main column of Cards plus a 320px
 * sidebar. Per-vehicle pricing - capacity, vehicle type and route are
 * first-class.
 *
 *   /transfers/new          mode=create
 *   /transfers/:id/edit     mode=edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const TRANSFER_MODE_OPTIONS = [
  { value: 'CAR', label: 'Car (per vehicle / SIC)' },
  { value: 'BUS', label: 'Bus (per person)' },
  { value: 'TRAIN', label: 'Train (per person)' },
  { value: 'FERRY', label: 'Ferry (per person)' },
];

// Trip shape. Public modes (bus/train/ferry) are point-to-point voyages, so
// only ONE_WAY / ROUND_TRIP apply; the hourly/day-hire/multi-stop shapes are
// car-only. `transferTypeOptionsFor` narrows the list per mode.
const TRANSFER_TYPE_OPTIONS = [
  { value: 'ONE_WAY', label: 'One-way' },
  { value: 'ROUND_TRIP', label: 'Round trip' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'DAY_HIRE', label: 'Day hire' },
  { value: 'MULTI_STOP', label: 'Multi-stop' },
];
const transferTypeOptionsFor = (mode: TransferMode): { value: string; label: string }[] =>
  isPublicTransferMode(mode)
    ? TRANSFER_TYPE_OPTIONS.filter((o) => o.value === 'ONE_WAY' || o.value === 'ROUND_TRIP')
    : TRANSFER_TYPE_OPTIONS;

const TRANSFER_CATEGORY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'AIRPORT', label: 'Airport transfer' },
  { value: 'HOTEL', label: 'Hotel transfer' },
  { value: 'INTERCITY', label: 'Intercity' },
  { value: 'OTHER', label: 'Other' },
];

const MARKUP_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat amount' },
];

const SERVICE_TYPE_OPTIONS = [
  { value: 'PRIVATE', label: 'Private (per vehicle)' },
  { value: 'SIC', label: 'Shared / SIC (per person)' },
];

/** A sellable rate row in the form. Maps 1:1 to TransferRateInput. */
interface RateFormRow {
  serviceType: TransferServiceType;
  shortDescription: string | null;
  longDescription: string | null;
  vehicleTypeId: string | null;
  /** Fare class / cabin - public modes (BUS/TRAIN/FERRY) only. */
  fareClassName: string | null;
  /** Days this rate is valid for once activated - multi-day pass rates only. */
  validityDays: number | null;
  label: string;
  paxMin: number;
  paxMax: number;
  maxLuggagePieces: number | null;
  pricePerVehicle: number;
  perPersonPrice: number;
  perPersonChildPrice: number | null;
  minPaxToOperate: number | null;
  isActive: boolean;
}

const EMPTY_PRIVATE_RATE: RateFormRow = {
  serviceType: 'PRIVATE',
  shortDescription: null,
  longDescription: null,
  vehicleTypeId: null,
  fareClassName: null,
  validityDays: null,
  label: '',
  paxMin: 1,
  paxMax: 4,
  maxLuggagePieces: null,
  pricePerVehicle: 0,
  perPersonPrice: 0,
  perPersonChildPrice: null,
  minPaxToOperate: null,
  isActive: true,
};

const EMPTY_SIC_RATE: RateFormRow = {
  serviceType: 'SIC',
  shortDescription: null,
  longDescription: null,
  vehicleTypeId: null,
  fareClassName: null,
  validityDays: null,
  label: 'SIC - per person',
  paxMin: 1,
  paxMax: 99,
  maxLuggagePieces: null,
  pricePerVehicle: 0,
  perPersonPrice: 0,
  perPersonChildPrice: null,
  minPaxToOperate: null,
  isActive: true,
};

/** A per-person fare/cabin rate for the public modes (BUS/TRAIN/FERRY). */
const EMPTY_FARE_RATE: RateFormRow = {
  serviceType: 'SIC',
  shortDescription: null,
  longDescription: null,
  vehicleTypeId: null,
  fareClassName: '',
  validityDays: null,
  label: '',
  paxMin: 1,
  paxMax: 99,
  maxLuggagePieces: null,
  pricePerVehicle: 0,
  perPersonPrice: 0,
  perPersonChildPrice: null,
  minPaxToOperate: null,
  isActive: true,
};

/** One day-option within a pass fare class - e.g. "3 day" at a given price. */
interface PassTierRow {
  validityDays: number | null;
  perPersonPrice: number;
}

/** A pass fare class (e.g. "Standard") holding several validity-day tiers. */
interface PassClassRow {
  fareClassName: string;
  tiers: PassTierRow[];
}

const EMPTY_PASS_CLASS: PassClassRow = {
  fareClassName: '',
  tiers: [{ validityDays: null, perPersonPrice: 0 }],
};

const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

const ALL_WEEKDAYS: Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const WEEKDAY_PRESETS: { label: string; days: Weekday[] }[] = [
  { label: 'All days', days: ALL_WEEKDAYS },
  { label: 'Weekdays', days: ['MON', 'TUE', 'WED', 'THU', 'FRI'] },
  { label: 'Weekends', days: ['SAT', 'SUN'] },
];

interface TransferFormValues {
  status: TransferDetail['status'];
  legacyCode: string | null;
  legacyType: string | null;
  slug: string | null;
  name: string;
  transferMode: TransferMode;
  transferType: TransferDetail['transferType'];
  transferCategory: 'AIRPORT' | 'HOTEL' | 'INTERCITY' | 'OTHER' | null;
  // Multi-day travel pass (Swiss Travel Pass / JR Pass) - country-scoped,
  // per-person, priced by fare class x validity-day tier. See `passClasses`.
  isMultiDayPass: boolean;
  // Global: when on, the itinerary builder lists this transfer for every city
  // (geography filter bypassed), still gated to ACTIVE + approved.
  isGlobal: boolean;
  // Multi-modal (BUS/TRAIN/FERRY) - blank for CAR.
  operatorName: string | null;
  vesselOrServiceName: string | null;
  ferryNights: number | null;
  boardBasis: string | null;
  // Route - free text (legacy), no location master
  fromLocationName: string;
  toLocationName: string;
  routeDistanceKm: number | null;
  estimatedDurationMinutes: number;
  // Scheduling clock-times
  pickupTime: string;
  scheduleStartTime: string;
  scheduleEndTime: string;
  // Origin
  cityId: string | null;
  countryId: string | null;
  // Vendor
  vendorSupplierId: string | null;
  contractRef: string | null;
  // Pricing - currency + markup only (per-rate amounts live on the rates).
  currencyId: string | null;
  markupType: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  // Inclusions
  isPickupIncluded: boolean;
  isDropOffIncluded: boolean;
  includesFuel: boolean;
  includesToll: boolean;
  includesDriverGratuity: boolean;
  // Logistics
  languageId: string | null;
  // Content
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  inclusionsHtml: string | null;
  exclusionsHtml: string | null;
  termsHtml: string | null;
  cancellationHtml: string | null;
  // Operational
  runOnWeekdays: Weekday[];
  validityFrom: string | null;
  validityTo: string | null;
  // Media
  photos: GalleryPhoto[];
  // Standalone 4-image gallery (admin-only), separate from `photos`.
  galleryImages: string[];
  // Misc
  rating: number;
  metaTagName: string | null;
  // B4.13: sellable rates (vehicle tiers + SIC).
  rates: RateFormRow[];
  // Multi-day pass editor only - flattened into `rates` at submit time
  // (see fromFormValues) and never sent to the server directly.
  passClasses: PassClassRow[];
}

const CREATE_DEFAULTS: TransferFormValues = {
  status: 'DRAFT',
  legacyCode: null,
  legacyType: null,
  slug: null,
  name: '',
  transferMode: 'CAR',
  transferType: 'ONE_WAY',
  transferCategory: 'AIRPORT',
  isMultiDayPass: false,
  isGlobal: false,
  operatorName: null,
  vesselOrServiceName: null,
  ferryNights: null,
  boardBasis: null,
  fromLocationName: '',
  toLocationName: '',
  routeDistanceKm: null,
  estimatedDurationMinutes: 0,
  pickupTime: '',
  scheduleStartTime: '',
  scheduleEndTime: '',
  cityId: null,
  countryId: null,
  vendorSupplierId: null,
  contractRef: null,
  currencyId: null,
  markupType: null,
  markupValue: 0,
  isPickupIncluded: true,
  isDropOffIncluded: true,
  includesFuel: true,
  includesToll: false,
  includesDriverGratuity: false,
  languageId: null,
  shortDescription: null,
  longDescriptionHtml: null,
  inclusionsHtml: null,
  exclusionsHtml: null,
  termsHtml: null,
  cancellationHtml: null,
  runOnWeekdays: [...ALL_WEEKDAYS],
  validityFrom: null,
  validityTo: null,
  photos: [],
  galleryImages: [],
  rating: 0,
  metaTagName: null,
  rates: [{ ...EMPTY_PRIVATE_RATE }],
  passClasses: [],
};

const parsePhotos = (a: unknown): GalleryPhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is GalleryPhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

const toFormValues = (t: TransferDetail | null): TransferFormValues => {
  if (!t) return CREATE_DEFAULTS;
  return {
    status: t.status,
    legacyCode: t.legacyCode,
    legacyType: t.legacyType,
    slug: t.slug,
    name: t.name,
    transferMode: t.transferMode,
    transferType: t.transferType,
    transferCategory: t.transferCategory,
    isMultiDayPass: t.isMultiDayPass ?? false,
    isGlobal: t.isGlobal ?? false,
    operatorName: t.operatorName,
    vesselOrServiceName: t.vesselOrServiceName,
    ferryNights: t.ferryNights,
    boardBasis: t.boardBasis,
    fromLocationName: t.fromLocationName ?? '',
    toLocationName: t.toLocationName ?? '',
    routeDistanceKm: t.routeDistanceKm,
    estimatedDurationMinutes: t.estimatedDurationMinutes,
    pickupTime: t.pickupTime ?? '',
    scheduleStartTime: t.scheduleStartTime ?? '',
    scheduleEndTime: t.scheduleEndTime ?? '',
    cityId: t.cityId,
    countryId: t.countryId,
    vendorSupplierId: t.vendorSupplierId,
    contractRef: t.contractRef,
    currencyId: t.currencyId,
    markupType: t.markupType === 'PERCENT' || t.markupType === 'FLAT' ? t.markupType : null,
    markupValue: t.markupValue,
    isPickupIncluded: t.isPickupIncluded,
    isDropOffIncluded: t.isDropOffIncluded,
    includesFuel: t.includesFuel,
    includesToll: t.includesToll,
    includesDriverGratuity: t.includesDriverGratuity,
    languageId: t.languageId,
    shortDescription: t.shortDescription,
    longDescriptionHtml: t.longDescriptionHtml,
    inclusionsHtml: t.inclusionsHtml,
    exclusionsHtml: t.exclusionsHtml,
    termsHtml: t.termsHtml,
    cancellationHtml: t.cancellationHtml,
    runOnWeekdays: t.runOnWeekdays,
    validityFrom: t.validityFrom,
    validityTo: t.validityTo,
    photos: parsePhotos(t.photos),
    galleryImages: Array.isArray(t.galleryImageMediaIds) ? t.galleryImageMediaIds : [],
    rating: t.rating,
    metaTagName: t.metaTagName,
    rates:
      t.rates.length > 0
        ? [...t.rates]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((r) => ({
              serviceType: r.serviceType,
              shortDescription: r.shortDescription,
              longDescription: r.longDescription,
              vehicleTypeId: r.vehicleTypeId,
              fareClassName: r.fareClassName,
              validityDays: r.validityDays ?? null,
              label: r.label,
              paxMin: r.paxMin,
              paxMax: r.paxMax,
              maxLuggagePieces: r.maxLuggagePieces,
              pricePerVehicle: r.pricePerVehicle,
              perPersonPrice: r.perPersonPrice,
              perPersonChildPrice: r.perPersonChildPrice,
              minPaxToOperate: r.minPaxToOperate,
              isActive: r.isActive,
            }))
        : [{ ...EMPTY_PRIVATE_RATE }],
    passClasses: t.isMultiDayPass ? groupRatesIntoPassClasses(t.rates) : [],
  };
};

/**
 * Group a pass's flat rates into fare-class cards for the nested editor -
 * each distinct fareClassName (in order of first appearance; null/empty
 * groups under '') becomes one PassClassRow, its rates becoming day-tiers.
 */
const groupRatesIntoPassClasses = (rates: TransferDetail['rates']): PassClassRow[] => {
  const sorted = [...rates].sort((a, b) => a.sortOrder - b.sortOrder);
  const classes: PassClassRow[] = [];
  const byName = new Map<string, PassClassRow>();
  for (const r of sorted) {
    const name = r.fareClassName ?? '';
    let cls = byName.get(name);
    if (!cls) {
      cls = { fareClassName: name, tiers: [] };
      byName.set(name, cls);
      classes.push(cls);
    }
    cls.tiers.push({ validityDays: r.validityDays ?? null, perPersonPrice: r.perPersonPrice ?? 0 });
  }
  return classes;
};

/**
 * Keep only keys whose value changed vs the original (JSON-compared). Lets the
 * edit form PATCH just the fields the user touched, so an image-only edit
 * publishes live without spawning a no-op pending-approval entry (mirrors the
 * catalog approval media split on the server).
 */
const pickChanged = <T extends Record<string, unknown>>(next: T, prev: T): T => {
  const out = {} as T;
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(prev[key])) out[key] = next[key];
  }
  return out;
};

const fromFormValues = (values: TransferFormValues): CreateTransferRequest => {
  const mode = values.transferMode;
  const isPublic = isPublicTransferMode(mode); // BUS/TRAIN/FERRY
  const isFerry = mode === 'FERRY';
  const isPass = values.isMultiDayPass;
  return {
    status: values.status,
    legacyCode: trimOrNull(values.legacyCode),
    legacyType: trimOrNull(values.legacyType),
    slug: trimOrNull(values.slug),
    name: values.name.trim(),
    transferMode: mode,
    transferType: values.transferType,
    isMultiDayPass: isPass,
    isGlobal: values.isGlobal,
    // Category / driver-language are CAR-world concepts - blanked for public modes.
    transferCategory: isPass ? null : isPublic ? null : values.transferCategory,
    // Multi-modal fields - only sent for the modes they belong to.
    operatorName: isPublic ? trimOrNull(values.operatorName) : null,
    vesselOrServiceName: isPublic ? trimOrNull(values.vesselOrServiceName) : null,
    ferryNights: isFerry ? (values.ferryNights ?? null) : null,
    boardBasis: isFerry ? trimOrNull(values.boardBasis) : null,
    // A pass is country-wide - no city and no free-text route.
    fromLocationName: isPass ? null : values.fromLocationName.trim() || null,
    toLocationName: isPass ? null : values.toLocationName.trim() || null,
    routeDistanceKm: values.routeDistanceKm,
    estimatedDurationMinutes: values.estimatedDurationMinutes,
    pickupTime: values.pickupTime.trim() || null,
    scheduleStartTime: values.scheduleStartTime.trim() || null,
    scheduleEndTime: values.scheduleEndTime.trim() || null,
    cityId: isPass ? null : (values.cityId ?? null),
    countryId: values.countryId ?? null,
    vendorSupplierId: values.vendorSupplierId ?? null,
    contractRef: trimOrNull(values.contractRef),
    currencyId: values.currencyId ?? null,
    markupType: values.markupType,
    markupValue: values.markupValue || 0,
    // Car-only inclusions - force off for public modes (no driver/vehicle/fuel).
    isPickupIncluded: isPublic ? false : values.isPickupIncluded,
    isDropOffIncluded: isPublic ? false : values.isDropOffIncluded,
    includesFuel: isPublic ? false : values.includesFuel,
    includesToll: isPublic ? false : values.includesToll,
    includesDriverGratuity: isPublic ? false : values.includesDriverGratuity,
    languageId: isPublic ? null : (values.languageId ?? null),
    shortDescription: trimOrNull(values.shortDescription),
    longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
    inclusionsHtml: trimOrNull(values.inclusionsHtml),
    exclusionsHtml: trimOrNull(values.exclusionsHtml),
    termsHtml: trimOrNull(values.termsHtml),
    cancellationHtml: trimOrNull(values.cancellationHtml),
    runOnWeekdays: values.runOnWeekdays,
    validityFrom: values.validityFrom || null,
    validityTo: values.validityTo || null,
    photos: values.photos.length > 0 ? values.photos : null,
    galleryImageMediaIds: values.galleryImages,
    rating: values.rating,
    metaTagName: trimOrNull(values.metaTagName),
    rates: isPass
      ? buildPassRates(values.passClasses)
      : values.rates.map((r, i): TransferRateInput => {
          // Public modes are per-person only: force SIC + drop the vehicle. CAR
          // keeps the operator's PRIVATE/SIC choice. This guarantees the server
          // refine passes.
          const serviceType: TransferServiceType = isPublic ? 'SIC' : r.serviceType;
          const isPrivate = serviceType === 'PRIVATE';
          return {
            serviceType,
            shortDescription: trimOrNull(r.shortDescription),
            longDescription: trimOrNull(r.longDescription),
            vehicleTypeId: isPrivate ? (r.vehicleTypeId ?? null) : null,
            fareClassName: isPublic ? trimOrNull(r.fareClassName) : null,
            validityDays: r.validityDays ?? null,
            label: r.label.trim() || (isPublic ? r.fareClassName?.trim() || 'Fare' : 'Rate'),
            paxMin: r.paxMin || 1,
            paxMax: r.paxMax || 1,
            maxLuggagePieces: r.maxLuggagePieces,
            pricePerVehicle: isPrivate ? r.pricePerVehicle || 0 : 0,
            perPersonPrice: isPrivate ? 0 : r.perPersonPrice || 0,
            perPersonChildPrice: isPrivate
              ? null
              : r.perPersonChildPrice
                ? r.perPersonChildPrice
                : null,
            minPaxToOperate: isPrivate ? null : r.minPaxToOperate,
            isBase: i === 0,
            isActive: r.isActive,
            sortOrder: i,
          };
        }),
  };
};

/**
 * Flatten the pass editor's class/day-tier grid into the flat TransferRateInput
 * list the server expects - one rate per (fare class, validityDays) pair.
 * Mirrors every field the non-pass rate map above produces so the DTO shape
 * is identical; only the values differ (per-person, no vehicle, SIC only).
 */
const buildPassRates = (classes: PassClassRow[]): TransferRateInput[] => {
  const rates: TransferRateInput[] = [];
  let sortOrder = 0;
  for (const cls of classes) {
    const className = cls.fareClassName.trim();
    for (const tier of cls.tiers) {
      rates.push({
        serviceType: 'SIC',
        shortDescription: null,
        longDescription: null,
        vehicleTypeId: null,
        fareClassName: className || null,
        validityDays: tier.validityDays,
        label: `${className || 'Pass'} - ${tier.validityDays ?? ''} day`.trim(),
        paxMin: 1,
        paxMax: 99,
        maxLuggagePieces: null,
        pricePerVehicle: 0,
        perPersonPrice: tier.perPersonPrice || 0,
        perPersonChildPrice: null,
        minPaxToOperate: null,
        isBase: sortOrder === 0,
        isActive: true,
        sortOrder: sortOrder++,
      });
    }
  }
  return rates;
};

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

/** "HH:MM" -> today's Date at that clock time, for UzDateTimePicker time mode. */
const hhmmToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [h, m] = s.split(':').map(Number);
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};
/** Date -> "HH:MM" (local). */
const dateToHhmm = (d: Date | null): string =>
  d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';

/** Shopify-style card: title header + stacked fields. */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

export const TransferEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/transfers');
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useTransferDetail(isCreate ? null : (id ?? null));
  const transfer = detailQuery.data ?? null;

  const createTransfer = useCreateTransfer();
  const updateTransfer = useUpdateTransfer();
  const approveTransfer = useApproveTransfer();
  const pending = createTransfer.isPending || updateTransfer.isPending || approveTransfer.isPending;

  const form = useForm<TransferFormValues>({ defaultValues: toFormValues(null) });

  const currenciesQuery = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc' });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (transfer) form.reset(toFormValues(transfer));
  }, [isCreate, transfer, form]);

  // Default the currency to INR on a fresh transfer once the list loads.
  useEffect(() => {
    if (!isCreate) return;
    if (form.getValues('currencyId')) return;
    const inr = currenciesQuery.data?.data.find((c) => c.code === 'INR');
    if (inr) form.setValue('currencyId', inr.id);
  }, [isCreate, currenciesQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!values.slug || !values.slug.trim()) {
      form.setError('slug', { message: 'URL slug is required (use the wand to generate)' });
      setActiveTab('details');
      return;
    }
    if (!values.countryId) {
      form.setError('countryId', { message: 'Country is required' });
      setActiveTab('details');
      return;
    }
    if (values.isMultiDayPass) {
      const classes = values.passClasses;
      const hasClass = classes.some((c) => c.fareClassName.trim());
      const hasTierPerClass = classes.every((c) => c.tiers.length > 0);
      const allTiersValid = classes.every((c) => c.tiers.every((t) => (t.validityDays ?? 0) > 0));
      if (classes.length === 0 || !hasClass || !hasTierPerClass || !allTiersValid) {
        form.setError('passClasses', {
          message:
            'Add at least one fare class with a name, and every day option needs a validity greater than 0.',
        });
        setActiveTab('details');
        return;
      }
    } else if (!values.cityId) {
      form.setError('cityId', { message: 'City is required' });
      setActiveTab('details');
      return;
    }
    // Transfer images (cover + gallery) are OPTIONAL - a transfer can be saved
    // with none. The itinerary day collage already treats transfer media as a
    // last-resort filler, so a transfer without photos never blocks a quote.
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createTransfer.mutateAsync(payload);
      navigate(`/transfers/${created.id}/edit`);
    } else if (transfer) {
      // PATCH only the fields the user actually changed (diff vs the loaded
      // transfer, via the same fromFormValues mapper so types line up).
      const diff = pickChanged(payload, fromFormValues(toFormValues(transfer)));
      await updateTransfer.mutateAsync({ id: transfer.id, payload: diff });
    }
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <RouteCard form={form} pending={pending} />
                <RatesCard form={form} pending={pending} />
                <PricingCard form={form} pending={pending} />
                <InclusionsCard form={form} pending={pending} />
              </div>
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                <StatusCard pending={pending} canApprove={canApprove} />
                <CommercialCard pending={pending} />
                <LogisticsCard form={form} pending={pending} />
              </aside>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'content',
        label: 'Content & media',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <ContentCard pending={pending} />
              <MediaCard pending={pending} />
              <GalleryCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit stable per render
  }, [isCreate, transfer, canApprove, form, pending]);

  const statusDot =
    transfer?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : transfer?.status === 'DRAFT' || transfer?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    transfer?.status === 'ACTIVE'
      ? 'Active'
      : transfer?.status === 'DRAFT'
        ? 'Draft'
        : transfer?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : transfer?.status === 'INACTIVE'
            ? 'Inactive'
            : transfer?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to transfers"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Transfers</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add transfer' : transfer ? transfer.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && transfer && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && transfer && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            {transfer.legacyId !== null && (
              <span>
                Legacy{' '}
                <span className="font-mono text-zinc-700 tabular-nums">#{transfer.legacyId}</span>
              </span>
            )}
            {transfer.legacyCode && (
              <span>
                Code <span className="font-mono text-zinc-700">{transfer.legacyCode}</span>
              </span>
            )}
          </div>
        )}

        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load transfer detail. Go back and retry.</UzAlert>
        )}

        {/* The fields below show the LAST APPROVED version, not the captured
            edit - without this banner the form looks like the save was lost. */}
        {transfer?.awaitingApproval && (
          <UzAlert tone="warning">
            <span className="font-medium">Waiting on an admin.</span> Your changes are saved and
            queued for approval
            {transfer.pendingSubmittedAt ? ` (${relativeTime(transfer.pendingSubmittedAt)})` : ''}.
            The fields below still show the live version until an admin approves.
          </UzAlert>
        )}

        {(isCreate || transfer) && (
          <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<TransferFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  const isMultiDayPass = Boolean(useWatch({ control: form.control, name: 'isMultiDayPass' }));
  const isPublic = isPublicTransferMode(mode);
  const isFerry = mode === 'FERRY';
  // Switching mode resets the rates to the right shape (per-vehicle vs
  // per-person fare) and blanks the fields the new mode doesn't use, so no
  // stale car/public data lingers. A pass drives its rates from passClasses
  // instead, so leave `rates` alone while isMultiDayPass is on.
  const onModeChange = (next: TransferMode): void => {
    form.setValue('transferMode', next, { shouldDirty: true });
    const nextPublic = isPublicTransferMode(next);
    if (!isMultiDayPass) {
      form.setValue('rates', [{ ...(nextPublic ? EMPTY_FARE_RATE : EMPTY_PRIVATE_RATE) }], {
        shouldDirty: true,
      });
    }
    if (nextPublic) {
      form.setValue('transferCategory', null);
      form.setValue('languageId', null);
    } else {
      form.setValue('operatorName', null);
      form.setValue('vesselOrServiceName', null);
    }
    if (next !== 'FERRY') {
      form.setValue('ferryNights', null);
      form.setValue('boardBasis', null);
    }
  };
  // A multi-day pass is public per-person transport - CAR is not selectable.
  const modeOptions = isMultiDayPass
    ? TRANSFER_MODE_OPTIONS.filter((o) => o.value !== 'CAR')
    : TRANSFER_MODE_OPTIONS;
  const onPassToggle = (checked: boolean): void => {
    form.setValue('isMultiDayPass', checked, { shouldDirty: true });
    if (checked) {
      if (mode === 'CAR') onModeChange('TRAIN');
      if (form.getValues('passClasses').length === 0) {
        form.setValue('passClasses', [{ ...EMPTY_PASS_CLASS }], { shouldDirty: true });
      }
      form.setValue('cityId', null, { shouldDirty: true });
    } else {
      form.setValue(
        'rates',
        [
          {
            ...(isPublicTransferMode(form.getValues('transferMode'))
              ? EMPTY_FARE_RATE
              : EMPTY_PRIVATE_RATE),
          },
        ],
        { shouldDirty: true },
      );
    }
  };
  return (
    <Card title="Identity">
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Transfer name"
            placeholder="e.g. Mumbai Airport to South Mumbai - Sedan"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="slug">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="URL slug"
            required
            error={error}
            disabled={pending}
            rightAdornment={
              <UzTooltip label="Auto-generate slug">
                <button
                  type="button"
                  disabled={pending}
                  aria-label="Auto-generate slug"
                  onClick={() => {
                    const name = (form.getValues('name') ?? '').trim();
                    if (name) field.onChange(slugify(name));
                  }}
                  className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                </button>
              </UzTooltip>
            }
          />
        )}
      </UzFormField>
      <UzFormField name="isMultiDayPass">
        {({ field }) => (
          <UzSwitch
            label="This is a multi-day travel pass (e.g. Swiss Travel Pass, JR Pass)"
            description="Country-wide, per-person pass sold by fare class and validity in days - not a point-to-point route."
            checked={Boolean(field.value)}
            onCheckedChange={onPassToggle}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isGlobal">
        {({ field }) => (
          <UzSwitch
            label="Global transfer (available in every city)"
            description="When on, this transfer appears in the itinerary builder's add-transfer picker for every city, not just its own. Still needs to be Active and approved to show."
            checked={Boolean(field.value)}
            onCheckedChange={(checked) => field.onChange(checked)}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="transferMode">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Transport mode"
              options={modeOptions}
              value={(field.value as string | undefined) || 'CAR'}
              onValueChange={(v) => onModeChange((v as TransferMode) || 'CAR')}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="transferType">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Transfer type"
              options={transferTypeOptionsFor(mode)}
              value={(field.value as string | undefined) || 'ONE_WAY'}
              onValueChange={(v) => field.onChange(v)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      {/* Sub-category is a car/bus airport-vs-hotel concept; hidden for train/ferry. */}
      {!isPublic && (
        <UzFormField name="transferCategory">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Sub-category"
              options={TRANSFER_CATEGORY_OPTIONS}
              value={(field.value as string | null | undefined) ?? ''}
              onValueChange={(v) =>
                field.onChange(v ? (v as 'AIRPORT' | 'HOTEL' | 'INTERCITY' | 'OTHER') : null)
              }
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      )}
      {/* Multi-modal fields - public transport brand + (ferry) voyage details. */}
      {isPublic && (
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name="operatorName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Operator"
                placeholder={
                  mode === 'TRAIN'
                    ? 'e.g. Eurostar / SNCF / JR'
                    : mode === 'FERRY'
                      ? 'e.g. Makruzz / Nautika'
                      : 'e.g. FlixBus'
                }
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="vesselOrServiceName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label={mode === 'FERRY' ? 'Ferry name' : 'Service name'}
                placeholder={mode === 'FERRY' ? 'e.g. Makruzz Gold' : 'e.g. Shinkansen Nozomi'}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      )}
      {isFerry && (
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name="ferryNights">
            {({ field, error }) => (
              <UzInput
                type="number"
                name={field.name}
                value={(field.value as number | null | undefined)?.toString() ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? null : Number(e.target.value))
                }
                label="Ferry nights"
                placeholder="e.g. 7"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="boardBasis">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Board basis"
                placeholder="e.g. Full board / All-inclusive"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      )}
      <UzFormField name="rating">
        {({ field }) => (
          <StarRatingInput
            label="Rating"
            value={(field.value as number) ?? 0}
            onChange={(v) => field.onChange(v)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </Card>
  );
};

const RouteCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const countryId = useWatch({ control: form.control, name: 'countryId' });
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  const isMultiDayPass = Boolean(useWatch({ control: form.control, name: 'isMultiDayPass' }));
  const fromPlaceholder =
    mode === 'FERRY'
      ? 'e.g. Embarkation port'
      : mode === 'TRAIN'
        ? 'e.g. Origin station'
        : mode === 'BUS'
          ? 'e.g. Boarding point'
          : 'e.g. Airport';
  const toPlaceholder =
    mode === 'FERRY'
      ? 'e.g. Disembarkation port'
      : mode === 'TRAIN'
        ? 'e.g. Destination station'
        : mode === 'BUS'
          ? 'e.g. Drop-off point'
          : 'e.g. Hotel';
  return (
    <Card title="Route & geography">
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="countryId">
          {({ field, error }) => (
            <CountryPicker
              label={isMultiDayPass ? 'Country (pass valid country-wide)' : 'Country'}
              hint={isMultiDayPass ? 'A pass is not tied to a single city.' : undefined}
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => {
                field.onChange(v);
                if (v !== countryId) form.setValue('cityId', null);
              }}
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        {!isMultiDayPass && (
          <UzFormField name="cityId">
            {({ field, error }) => (
              <CityPicker
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => field.onChange(v)}
                countryId={countryId ?? null}
                required
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>
        )}
      </div>
      {!isMultiDayPass && (
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name="fromLocationName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | undefined) ?? ''}
                label="From"
                placeholder={fromPlaceholder}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="toLocationName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | undefined) ?? ''}
                label="To"
                placeholder={toPlaceholder}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="routeDistanceKm">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Route distance (km)"
              precision={1}
              min={0}
              value={
                field.value === null || field.value === undefined ? '' : (field.value as number)
              }
              onValueChange={(v) => field.onChange(v ?? null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="estimatedDurationMinutes">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Duration (minutes)"
              precision={0}
              min={0}
              value={
                field.value === null || field.value === undefined ? 0 : (field.value as number)
              }
              onValueChange={(v) => field.onChange(v ?? 0)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      {/* Schedule clock-times (legacy). End auto-fills from start + duration on import; editable. */}
      <div className="grid gap-5 sm:grid-cols-3">
        <UzFormField name="pickupTime">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="time"
              label="Pickup time"
              value={hhmmToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="scheduleStartTime">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="time"
              label="Start time"
              value={hhmmToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="scheduleEndTime">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="time"
              label="End time"
              value={hhmmToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <WeekdaysToggle pending={pending} />
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="validityFrom">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="date"
              displayFormat="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              label="Valid from"
              value={ymdToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="validityTo">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="date"
              displayFormat="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              label="Valid to"
              value={ymdToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
    </Card>
  );
};

const WeekdaysToggle = ({ pending }: CardProps): React.JSX.Element => (
  <UzFormField name="runOnWeekdays">
    {({ field }) => {
      const current = (field.value as Weekday[] | undefined) ?? [];
      const key = (arr: ReadonlyArray<string>): string => [...arr].sort().join(',');
      const toggle = (day: Weekday) => {
        if (current.includes(day)) field.onChange(current.filter((d) => d !== day));
        else field.onChange([...current, day]);
      };
      return (
        <div>
          <span className="mb-2 block text-sm leading-none font-medium text-zinc-900">
            Operating days
          </span>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {WEEKDAY_PRESETS.map((p) => {
              const active = key(current) === key(p.days);
              return (
                <button
                  key={p.label}
                  type="button"
                  disabled={pending}
                  onClick={() => field.onChange([...p.days])}
                  className={
                    'h-8 rounded-md border px-3 text-xs font-medium transition-colors ' +
                    (active
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                  }
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_OPTIONS.map((d) => {
              const on = current.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  disabled={pending}
                  onClick={() => toggle(d.value)}
                  className={
                    'h-8 w-11 rounded-md border text-xs font-medium transition-colors ' +
                    (on
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                  }
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            All days selected by default. Empty = bookable any day.
          </p>
        </div>
      );
    }}
  </UzFormField>
);

const PricingCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const markupType = useWatch({ control: form.control, name: 'markupType' });
  return (
    <Card
      title="Currency & markup"
      description="Rate amounts are entered per rate above, in this currency. The markup applies on top of every rate's base cost."
    >
      <UzFormField name="currencyId">
        {({ field, error }) => (
          <CurrencyPicker
            value={(field.value as string | null | undefined) ?? null}
            onValueChange={(v) => field.onChange(v)}
            disabled={pending}
            error={error}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="markupType">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Markup type"
              options={MARKUP_OPTIONS}
              value={(field.value as string | null | undefined) || ''}
              onValueChange={(v) => field.onChange(v === '' ? null : (v as 'PERCENT' | 'FLAT'))}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        {markupType && (
          <UzFormField name="markupValue">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label={markupType === 'PERCENT' ? 'Markup %' : 'Markup amount'}
                precision={2}
                min={0}
                rightAdornment={
                  markupType === 'PERCENT' ? (
                    <span className="text-sm text-zinc-500">%</span>
                  ) : undefined
                }
                value={(field.value as number | undefined) || ''}
                onValueChange={(v) => field.onChange(v ?? 0)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        )}
      </div>
    </Card>
  );
};

const RateRow = ({
  form,
  index,
  isBase,
  pending,
  onRemove,
}: {
  form: UseFormReturn<TransferFormValues>;
  index: number;
  isBase: boolean;
  pending: boolean;
  onRemove: () => void;
}): React.JSX.Element => {
  // useWatch (not form.watch) so THIS row re-renders the moment the service
  // type flips - form.watch only re-renders the component that owns useForm,
  // which left the per-vehicle / per-person fields showing the old shape.
  const serviceType = useWatch({ control: form.control, name: `rates.${index}.serviceType` });
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  const isPublic = isPublicTransferMode(mode);
  // Public modes are always per-person (SIC); only CAR can be PRIVATE.
  const isPrivate = !isPublic && serviceType === 'PRIVATE';
  return (
    <div className="space-y-4 rounded-md border border-zinc-200 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          {isBase ? 'Base rate' : `Rate ${index + 1}`}
        </span>
        {!isBase && (
          <button
            type="button"
            onClick={onRemove}
            disabled={pending}
            aria-label="Remove rate"
            className="text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {isPublic ? (
          // Public modes: the fare class / cabin IS the rate identity - no
          // PRIVATE/SIC choice, and it doubles as the label.
          <UzFormField name={`rates.${index}.fareClassName`}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label={mode === 'FERRY' ? 'Cabin category' : 'Fare class'}
                placeholder={
                  mode === 'FERRY'
                    ? 'e.g. Balcony / Interior / Suite'
                    : mode === 'TRAIN'
                      ? 'e.g. Standard / First class'
                      : 'e.g. Standard / Luxury coach'
                }
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        ) : (
          <>
            <UzFormField name={`rates.${index}.serviceType`}>
              {({ field, error }) => (
                <UzSelect
                  name={field.name}
                  label="Service type"
                  options={SERVICE_TYPE_OPTIONS}
                  value={(field.value as string | undefined) ?? 'PRIVATE'}
                  onValueChange={(v) => field.onChange((v as TransferServiceType) || 'PRIVATE')}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name={`rates.${index}.label`}>
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                  label="Label"
                  placeholder={isPrivate ? 'e.g. Sedan (1-3 pax)' : 'SIC - per person'}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </>
        )}
      </div>
      <UzFormField name={`rates.${index}.shortDescription`}>
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Short description"
            placeholder="One-sentence summary for picker cards."
            rows={2}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      {/* Rich text: shown under the day's photos on the customer share page.
          Older rows hold plain text, lifted into paragraphs for the editor. */}
      <UzFormField name={`rates.${index}.longDescription`}>
        {({ field }) => (
          <RichTextEditor
            label="Long description"
            placeholder="Multi-paragraph description with formatting..."
            value={textToHtml(field.value as string | null | undefined)}
            onChange={(html) => field.onChange(html === '' ? null : html)}
            disabled={pending}
            folder="transfers"
            size="compact"
          />
        )}
      </UzFormField>
      {isPrivate && (
        <UzFormField name={`rates.${index}.vehicleTypeId`}>
          {({ field, error }) => (
            <VehicleTypePicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => field.onChange(v)}
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name={`rates.${index}.paxMin`}>
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Pax min"
              min={1}
              precision={0}
              value={(field.value as number | undefined) ?? 1}
              onValueChange={(v) => field.onChange(v ?? 1)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name={`rates.${index}.paxMax`}>
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Pax max (per vehicle)"
              min={1}
              precision={0}
              value={(field.value as number | undefined) ?? 1}
              onValueChange={(v) => field.onChange(v ?? 1)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {isPrivate ? (
          <>
            <UzFormField name={`rates.${index}.pricePerVehicle`}>
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Price per vehicle (base cost)"
                  min={0}
                  precision={2}
                  value={(field.value as number | undefined) || ''}
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name={`rates.${index}.maxLuggagePieces`}>
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Max luggage (pieces)"
                  min={0}
                  precision={0}
                  value={(field.value as number | null | undefined) ?? ''}
                  onValueChange={(v) => field.onChange(v ?? null)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </>
        ) : (
          <>
            <UzFormField name={`rates.${index}.perPersonPrice`}>
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Adult price per seat"
                  min={0}
                  precision={2}
                  value={(field.value as number | undefined) || ''}
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name={`rates.${index}.perPersonChildPrice`}>
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Child price per seat (age 2-8)"
                  hint="Leave blank to charge the adult price. Children over 8 always pay adult."
                  min={0}
                  precision={2}
                  value={(field.value as number | null | undefined) ?? ''}
                  onValueChange={(v) => field.onChange(v ?? null)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name={`rates.${index}.minPaxToOperate`}>
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Min pax to operate"
                  min={1}
                  precision={0}
                  value={(field.value as number | null | undefined) ?? ''}
                  onValueChange={(v) => field.onChange(v ?? null)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </>
        )}
      </div>
      <UzFormField name={`rates.${index}.isActive`}>
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Uncheck to keep the rate on record but hide it from the itinerary builder."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  );
};

/** One validity-day tier inside a pass fare class - "3 day", "8 day" etc. */
const PassTierField = ({
  classIndex,
  tierIndex,
  pending,
  onRemove,
  removeDisabled,
}: {
  classIndex: number;
  tierIndex: number;
  pending: boolean;
  onRemove: () => void;
  removeDisabled: boolean;
}): React.JSX.Element => (
  <div className="space-y-3 rounded border border-zinc-100 p-3">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-zinc-500">Day option {tierIndex + 1}</span>
      {!removeDisabled && (
        <button
          type="button"
          onClick={onRemove}
          disabled={pending}
          aria-label="Remove day option"
          className="text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name={`passClasses.${classIndex}.tiers.${tierIndex}.validityDays`}>
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Validity (days)"
            min={1}
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name={`passClasses.${classIndex}.tiers.${tierIndex}.perPersonPrice`}>
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Price per person"
            min={0}
            precision={2}
            value={(field.value as number | undefined) || ''}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </div>
);

/**
 * One fare class card for a multi-day pass, e.g. "Standard" holding a 3 day
 * / 5 day / 8 day tier each with its own price. Owns its own nested
 * useFieldArray over `passClasses.${classIndex}.tiers` - hooks can't be
 * called conditionally in a loop, so this is a per-index extracted
 * component, the same pattern RateRow already follows for `rates`.
 */
const PassClassCard = ({
  form,
  classIndex,
  pending,
  onRemoveClass,
  removeDisabled,
}: {
  form: UseFormReturn<TransferFormValues>;
  classIndex: number;
  pending: boolean;
  onRemoveClass: () => void;
  removeDisabled: boolean;
}): React.JSX.Element => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `passClasses.${classIndex}.tiers`,
  });
  return (
    <div className="space-y-4 rounded-md border border-zinc-200 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          Fare class {classIndex + 1}
        </span>
        {!removeDisabled && (
          <button
            type="button"
            onClick={onRemoveClass}
            disabled={pending}
            aria-label="Remove class"
            className="text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <UzFormField name={`passClasses.${classIndex}.fareClassName`}>
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Fare class"
            placeholder="e.g. Standard / First class"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="space-y-3">
        {fields.map((f, tierIndex) => (
          <PassTierField
            key={f.id}
            classIndex={classIndex}
            tierIndex={tierIndex}
            pending={pending}
            onRemove={() => remove(tierIndex)}
            removeDisabled={fields.length <= 1}
          />
        ))}
      </div>
      <UzButton
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => append({ validityDays: null, perPersonPrice: 0 })}
        disabled={pending}
      >
        <Plus className="h-4 w-4" />
        <span>Add day option</span>
      </UzButton>
    </div>
  );
};

const RatesCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'rates' });
  const passClasses = useFieldArray({ control: form.control, name: 'passClasses' });
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  const isPublic = isPublicTransferMode(mode);
  const isMultiDayPass = Boolean(useWatch({ control: form.control, name: 'isMultiDayPass' }));

  if (isMultiDayPass) {
    return (
      <Card
        title="Fare classes & validity"
        description="A pass sells by fare class (e.g. Standard, First class) and one or more validity-day options (e.g. 3 day, 8 day), each with its own per-person price. Markup is applied from the Pricing card."
      >
        <div className="space-y-4">
          {passClasses.fields.map((f, index) => (
            <PassClassCard
              key={f.id}
              form={form}
              classIndex={index}
              pending={pending}
              onRemoveClass={() => passClasses.remove(index)}
              removeDisabled={passClasses.fields.length <= 1}
            />
          ))}
        </div>
        <UzButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => passClasses.append({ ...EMPTY_PASS_CLASS })}
          disabled={pending}
        >
          <Plus className="h-4 w-4" />
          <span>Add fare class</span>
        </UzButton>
      </Card>
    );
  }

  return (
    <Card
      title={isPublic ? (mode === 'FERRY' ? 'Cabins & fares' : 'Fares') : 'Rates & vehicles'}
      description={
        isPublic
          ? `One ${mode.toLowerCase()} route, priced per person by ${mode === 'FERRY' ? 'cabin category' : 'fare class'}. Add one row per ${mode === 'FERRY' ? 'cabin' : 'class'}. Markup is applied from the Pricing card. The first row is the base.`
          : 'One route, many sellable options. PRIVATE rows are priced per vehicle with a pax window (the itinerary builder multiplies vehicles for larger groups); SIC rows are per person. Markup is applied from the Pricing card. The first row is the base.'
      }
    >
      <div className="space-y-4">
        {fields.map((f, index) => (
          <RateRow
            key={f.id}
            form={form}
            index={index}
            isBase={index === 0}
            pending={pending}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {isPublic ? (
          <UzButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => append({ ...EMPTY_FARE_RATE })}
            disabled={pending}
          >
            <Plus className="h-4 w-4" />
            <span>{mode === 'FERRY' ? 'Add cabin' : 'Add fare class'}</span>
          </UzButton>
        ) : (
          <>
            <UzButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => append({ ...EMPTY_PRIVATE_RATE })}
              disabled={pending}
            >
              <Plus className="h-4 w-4" />
              <span>Add vehicle rate</span>
            </UzButton>
            <UzButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => append({ ...EMPTY_SIC_RATE })}
              disabled={pending}
            >
              <Plus className="h-4 w-4" />
              <span>Add SIC rate</span>
            </UzButton>
          </>
        )}
      </div>
    </Card>
  );
};

const InclusionsCard = ({ form, pending }: FormCardProps): React.JSX.Element | null => {
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  // Pickup/fuel/toll/driver-gratuity are driver-and-vehicle concepts. Public
  // modes (bus/train/cruise) have none of them, so the whole card is hidden.
  if (isPublicTransferMode(mode)) return null;
  return (
    <Card title="What's included" description="Toggles drive the docket's inclusions checklist.">
      <UzFormField name="isPickupIncluded">
        {({ field }) => (
          <UzSwitch
            label="Pickup included"
            description="Origin pickup is part of the contracted price."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isDropOffIncluded">
        {({ field }) => (
          <UzSwitch
            label="Drop-off included"
            description="Destination drop-off is part of the contracted price."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="includesFuel">
        {({ field }) => (
          <UzSwitch
            label="Fuel included"
            description="Customer is not charged separately for fuel."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="includesToll">
        {({ field }) => (
          <UzSwitch
            label="Toll included"
            description="Customer is not charged separately for tolls."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="includesDriverGratuity">
        {({ field }) => (
          <UzSwitch
            label="Driver gratuity included"
            description="Driver tip pre-bundled. If off, the docket shows a recommended tip."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </Card>
  );
};

// ----- Sidebar cards -----

interface StatusCardProps extends CardProps {
  canApprove: boolean;
}

const StatusCard = ({ pending, canApprove }: StatusCardProps): React.JSX.Element => (
  <Card title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const CommercialCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Commercial">
    <UzFormField name="vendorSupplierId">
      {({ field, error }) => (
        <SupplierPicker
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
    <UzFormField name="contractRef">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Contract reference"
          placeholder="Internal contract / PO number"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const LogisticsCard = ({ form, pending }: FormCardProps): React.JSX.Element | null => {
  const mode = (useWatch({ control: form.control, name: 'transferMode' }) ?? 'CAR') as TransferMode;
  // Driver language only makes sense for a car with a driver.
  if (isPublicTransferMode(mode)) return null;
  return (
    <Card title="Logistics">
      <UzFormField name="languageId">
        {({ field, error }) => (
          <LanguagePicker
            label="Driver language"
            value={(field.value as string | null | undefined) ?? null}
            onValueChange={(v) => field.onChange(v)}
            disabled={pending}
            error={error}
          />
        )}
      </UzFormField>
    </Card>
  );
};

// ----- Content & media -----

const ContentCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Descriptions & policy">
    {/* 2026-07-09. Short + long description moved to the per-rate editor
        (Rates card). The transfer-level values are still loaded and saved
        untouched so existing data / website rendering is preserved - they
        just no longer have inputs here. */}
    <div className="grid gap-5 md:grid-cols-2">
      <UzFormField name="inclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Inclusions copy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="exclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Exclusions copy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="termsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Terms & conditions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="cancellationHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Cancellation policy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Photos">
    {/* Cover only. The old multi-image "Gallery" was removed here in favour of
        the dedicated 4-box Gallery card below. We edit just the cover
        (photos sortOrder 0) and PRESERVE any other existing photos in the
        array so legacy gallery data is never dropped on save. */}
    <UzFormField name="photos">
      {({ field, error }) => {
        const photos = (field.value as GalleryPhoto[]) ?? [];
        const cover = photos.find((p) => (p.sortOrder ?? 0) === 0) ?? null;
        const setCover = (mediaId: string | null): void => {
          const rest = photos.filter((p) => (p.sortOrder ?? 0) !== 0);
          field.onChange(mediaId ? [{ mediaId, sortOrder: 0 }, ...rest] : rest);
        };
        return (
          <div className="space-y-1">
            <MediaDropField
              label="Cover photo"
              value={cover?.mediaId ?? null}
              onChange={setCover}
              folder="transfers"
              variant="banner"
              disabled={pending}
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        );
      }}
    </UzFormField>
  </Card>
);

/**
 * Standalone gallery - a separate section of up to 4 equal image slots
 * (no cover, no orientation). Always shows 4 boxes; drag a filled tile to
 * reorder. Optional - a transfer can be saved with none. Stored in the
 * Transfer's dedicated `galleryImageMediaIds` field, independent of the
 * Photos card above.
 */
const GalleryCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Gallery" description="Up to 4 images (optional). Drag a tile to reorder.">
    <UzFormField name="galleryImages">
      {({ field, error }) => (
        <div className="space-y-1">
          <FixedMediaSlotsField
            value={(field.value as string[] | null | undefined) ?? []}
            onChange={(ids) => field.onChange(ids)}
            count={4}
            gridClassName="grid grid-cols-2 gap-3 sm:grid-cols-4"
            aspectClassName="aspect-square"
            folder="transfers"
            disabled={pending}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </UzFormField>
  </Card>
);
